import React from "react";
import CardShiftList from "./CardShiftList";
import { ISwapRequestResponse } from "..";
import moment from "moment";
import { Icon } from "@iconify/react";

interface SwapListingProps {
  requests: ISwapRequestResponse[];
  outgoingProposals: any[];
  allProposals?: any[];
  myId?: string;
  onCancel: (id: string) => void;
}

const fmtDate = (day: number, month: number, year: number) =>
  moment({ year, month: month - 1, day }).format("DD MMMM YYYY");

const SwapListing = ({ requests, outgoingProposals, allProposals = [], myId, onCancel }: SwapListingProps) => {
  const hasProposals = outgoingProposals.length > 0;
  const hasRequests = requests.length > 0;

  // Accepted or rejected proposals that I was involved in (either role)
  const history = allProposals.filter(
    (p: any) => p.status === "accepted" || p.status === "rejected"
  );
  const hasHistory = history.length > 0;

  if (!hasProposals && !hasRequests && !hasHistory) return null;

  return (
    <div className="space-y-6">
      {/* Bilateral swap proposals sent by this nurse */}
      {hasProposals && (
        <div>
          <h3 className="text-moon-16 font-semibold text-bulma mb-3">Swap Requests Sent</h3>
          <div className="rounded-[8px] bg-goku overflow-hidden">
            {/* Header */}
            <div className="flex flex-wrap items-center mb-[4px] text-moon-14 font-semibold text-bulma">
              <div className="flex-[0_0_160px] p-[14px_16px]">Requested On</div>
              <div className="flex-[0_0_200px] p-[14px_16px]">Sent To</div>
              <div className="flex-[0_0_200px] p-[14px_16px]">Your Shift</div>
              <div className="flex-[0_0_200px] p-[14px_16px]">Their Shift</div>
              <div className="flex-1 p-[14px_16px]">Status</div>
            </div>

            {outgoingProposals.map((p: any) => {
              const colleague = p.colleague;
              const colleagueName = colleague
                ? `${colleague.firstName || ""} ${colleague.lastName || ""}`.trim()
                : "—";
              const rs = p.requesterShift;
              const cs = p.colleagueShift;
              const statusColor =
                p.status === "accepted"
                  ? "text-green-600"
                  : p.status === "rejected"
                  ? "text-red-500"
                  : "text-krillin";
              const statusLabel =
                p.status === "accepted"
                  ? "Accepted"
                  : p.status === "rejected"
                  ? "Declined"
                  : "Pending";

              return (
                <div
                  key={p._id}
                  className="flex flex-wrap items-center bg-white rounded-[8px] overflow-hidden mb-[4px] text-moon-14"
                >
                  <div className="flex-[0_0_160px] p-[14px_16px] text-trunks">
                    {moment(p.createdAt).format("DD MMM YYYY")}
                  </div>
                  <div className="flex-[0_0_200px] p-[14px_16px] font-medium">
                    {colleagueName}
                  </div>
                  <div className="flex-[0_0_200px] p-[14px_16px]">
                    {rs ? (
                      <span className={`inline-flex items-center gap-1 font-semibold ${rs.shiftType?.includes("night") ? "text-bulma" : "text-krillin"}`}>
                        <Icon icon={rs.shiftType?.includes("night") ? "uil:moon" : "uil:sun"} />
                        {fmtDate(rs.day, rs.month, rs.year)}
                      </span>
                    ) : "—"}
                  </div>
                  <div className="flex-[0_0_200px] p-[14px_16px]">
                    {cs ? (
                      <span className={`inline-flex items-center gap-1 font-semibold ${cs.shiftType?.includes("night") ? "text-bulma" : "text-krillin"}`}>
                        <Icon icon={cs.shiftType?.includes("night") ? "uil:moon" : "uil:sun"} />
                        {fmtDate(cs.day, cs.month, cs.year)}
                      </span>
                    ) : "—"}
                  </div>
                  <div className={`flex-1 p-[14px_16px] font-semibold ${statusColor}`}>
                    {statusLabel}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Swap history — completed (accepted/rejected) proposals */}
      {hasHistory && (
        <div>
          <h3 className="text-moon-16 font-semibold text-bulma mb-3">Swap History</h3>
          <div className="rounded-[8px] bg-goku overflow-hidden">
            <div className="flex flex-wrap items-center mb-[4px] text-moon-14 font-semibold text-bulma">
              <div className="flex-[0_0_160px] p-[14px_16px]">Date</div>
              <div className="flex-[0_0_200px] p-[14px_16px]">With</div>
              <div className="flex-[0_0_200px] p-[14px_16px]">Gave</div>
              <div className="flex-[0_0_200px] p-[14px_16px]">Received</div>
              <div className="flex-1 p-[14px_16px]">Status</div>
            </div>
            {history.map((p: any) => {
              const iAmRequester = p.requester?._id === myId || p.requester === myId;
              const counterpart = iAmRequester ? p.colleague : p.requester;
              const counterpartName = counterpart
                ? `${counterpart.firstName || ""} ${counterpart.lastName || ""}`.trim()
                : "—";
              // From my perspective: I gave my shift, I received the other shift
              const myShift   = iAmRequester ? p.requesterShift : p.colleagueShift;
              const theirShift = iAmRequester ? p.colleagueShift : p.requesterShift;
              const statusColor = p.status === "accepted" ? "text-green-600" : "text-red-500";
              const statusLabel = p.status === "accepted" ? "Completed" : "Declined";

              return (
                <div
                  key={p._id}
                  className="flex flex-wrap items-center bg-white rounded-[8px] overflow-hidden mb-[4px] text-moon-14"
                >
                  <div className="flex-[0_0_160px] p-[14px_16px] text-trunks">
                    {moment(p.createdAt).format("DD MMM YYYY")}
                  </div>
                  <div className="flex-[0_0_200px] p-[14px_16px] font-medium">{counterpartName}</div>
                  <div className="flex-[0_0_200px] p-[14px_16px]">
                    {myShift ? (
                      <span className={`inline-flex items-center gap-1 font-semibold ${myShift.shiftType?.includes("night") ? "text-bulma" : "text-krillin"}`}>
                        <Icon icon={myShift.shiftType?.includes("night") ? "uil:moon" : "uil:sun"} />
                        {fmtDate(myShift.day, myShift.month, myShift.year)}
                      </span>
                    ) : "—"}
                  </div>
                  <div className="flex-[0_0_200px] p-[14px_16px]">
                    {theirShift ? (
                      <span className={`inline-flex items-center gap-1 font-semibold ${theirShift.shiftType?.includes("night") ? "text-bulma" : "text-krillin"}`}>
                        <Icon icon={theirShift.shiftType?.includes("night") ? "uil:moon" : "uil:sun"} />
                        {fmtDate(theirShift.day, theirShift.month, theirShift.year)}
                      </span>
                    ) : "—"}
                  </div>
                  <div className={`flex-1 p-[14px_16px] font-semibold ${statusColor}`}>{statusLabel}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Legacy open swap requests */}
      {hasRequests && (
        <div>
          <h3 className="text-moon-16 font-semibold text-bulma mb-3">Open Swap Requests</h3>
          <div className="rounded-[8px] bg-goku overflow-hidden">
            <div className="flex flex-wrap items-center mb-[4px]">
              <div className="2xl:flex-[0_0_246px] flex-[0_0_200px] p-[17px_16px]">
                <h3 className="font-semibold text-blackcst text-moon-18">Requested On</h3>
              </div>
              <div className="2xl:flex-[0_0_246px] flex-[0_0_200px] p-[17px_16px]">
                <h3 className="font-semibold text-blackcst text-moon-18">Shift Date</h3>
              </div>
              <div className="2xl:flex-[0_0_246px] flex-[0_0_200px] p-[17px_16px]">
                <h3 className="font-semibold text-blackcst text-moon-18">Shift</h3>
              </div>
              <div className="flex-1 p-[17px_16px]"></div>
            </div>
            <div>
              {requests.map((card, index) => (
                <CardShiftList key={index} {...card} onCancel={onCancel} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SwapListing;
